<script setup lang="ts">
import type { MenuItem, MenuItemGroup, MenuItemLink } from '@global-types/nav/menuItem';
import { useUserProfile } from '@organisms/ubiq-administration/composables/useUserProfile';
import { trans } from '@elements/translation';
import { URL_LOGOUT } from '@elements/tools/routes/routes';
import { useNavbarMenu } from '../useNavbarMenu';
import AccountManagerMenu from '../AccountManagerMenu.vue';
import { ACCOUNT_PROFILE, PROFILE_MENU } from '../../config';
import MobileVerticalMenuLink from './MobileVerticalMenu/MobileVerticalMenuLink.vue';
import MobileVerticalMenuButton from './MobileVerticalMenu/MobileVerticalMenuButton.vue';
import MobileVerticalMenuDropdown from './MobileVerticalMenu/MobileVerticalMenuDropdown.vue';

const {
  user,
  userProfile,
  isProfileManagement,
  hasKYCActive,
  hasPayBuyer,
} = useUserProfile();

const {
  menuItems,
  isMenuOpen,
  toggleMenu,
  hasSubmenus,
  isDivider,
  isMenuLink,
  showMenuLink,
  getNotificationsCount,
  toggleMenuPreference,
  wordingSwitchButton,
} = useNavbarMenu();

const menuHelpSubmenus = window.HELP_MENU as MenuItem[];
</script>

<template>
  <ul class="mobile-vertical-menu">
    <li
      v-for="menuItem in menuItems"
      :key="menuItem.name"
    >
      <MobileVerticalMenuDropdown
        v-if="hasSubmenus(menuItem)"
        :icon="(menuItem as MenuItemGroup).icon"
        :has-icon-stroke="(menuItem as MenuItemGroup).hasIconStroke"
        :is-open="isMenuOpen(menuItem.name)"
        :submenus="(menuItem as MenuItemGroup).submenus"
        :is-profile-management="isProfileManagement"
        :has-kyc-active="hasKYCActive"
        :has-pay-buyer="hasPayBuyer"
        @toggle-menu="() => toggleMenu(menuItem.name)"
      >
        {{ menuItem.name }}
      </MobileVerticalMenuDropdown>

      <MobileVerticalMenuLink
        v-else-if="isMenuLink(menuItem) && showMenuLink(menuItem as MenuItemLink, userProfile)"
        :link="(menuItem as MenuItemLink).link"
        :icon="(menuItem as MenuItemLink).icon"
        :has-icon-stroke="(menuItem as MenuItemLink).hasIconStroke"
        :notifications-count="getNotificationsCount((menuItem as MenuItemLink).notification)"
      >
        {{ menuItem.name }}
      </MobileVerticalMenuLink>

      <hr
        v-else-if="isDivider(menuItem)"
        class="mobile-vertical-menu__divider"
      >
    </li>

    <li v-if="userProfile === ACCOUNT_PROFILE.BOTH" class="side-bar-menu__item">
      <MobileVerticalMenuButton
        icon="ph_shuffle"
        @click="toggleMenuPreference"
      >
        {{ wordingSwitchButton }}
      </MobileVerticalMenuButton>
    </li>

    <hr class="mobile-vertical-menu__divider">

    <li v-if="user">
      <AccountManagerMenu :account-manager="user.account_manager" />
    </li>
    <li class="mobile-vertical-menu__help">
      <MobileVerticalMenuDropdown
        icon="ph_question-circle"
        :is-open="isMenuOpen('help')"
        :submenus="menuHelpSubmenus"
        :is-profile-management="isProfileManagement"
        :has-kyc-active="hasKYCActive"
        has-submenus-border
        @toggle-menu="() => toggleMenu('help')"
      >
        {{ trans('user_menu.help') }}
      </MobileVerticalMenuDropdown>
    </li>

    <template v-if="userProfile === ACCOUNT_PROFILE.NO_PROFILE">
      <li
        v-for="menu in PROFILE_MENU"
        :key="menu.name"
      >
        <MobileVerticalMenuLink
          :link="(menu as MenuItemLink).link"
          :icon="(menu as MenuItemLink).icon"
        >
          {{ menu.name }}
        </MobileVerticalMenuLink>
      </li>
    </template>

    <li class="mobile-vertical-menu__bottom">
      <a
        class="btn btn--secondary"
        :href="URL_LOGOUT"
      >
        {{ trans('global.disconnect') }}
      </a>
    </li>
  </ul>
</template>
